import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Tabs, Tab } from '@mui/material';
import * as XLSX from 'xlsx';
import { FilePreviewError } from '../filePreviewError';
import { useExcelDocument } from 'hooks/useFileType/useExcelDocument';
import {
  CONTAINER_SX,
  CONTENT_SX,
  getCellStyle,
  LOADING_SX,
  MAX_CELLS_TO_RENDER,
  TABLE_STYLE,
  TABLE_WRAPPER_SX,
  TABS_SX,
} from './styles';
import { CellData, Props, WorksheetData } from './types';

const sanitizeCellValue = (cellValue: any): string => {
  return String(cellValue || '')
    .replace(/[<>]/g, '') // Remove angle brackets
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, ''); // Remove event handlers
};

export const ExcelDisplay: React.FC<Props> = ({ src, onDownload }) => {
  const { loading, error, workbook } = useExcelDocument(src);
  const [worksheets, setWorksheets] = useState<WorksheetData[]>([]);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (!workbook) return;

    const worksheetsData: WorksheetData[] = [];

    workbook.SheetNames.slice(0, 20).forEach((sheetName) => {
      const worksheet = workbook.Sheets[sheetName];
      const jsonData: any[][] = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: '',
        raw: false,
      });

      const limitedData = jsonData
        .slice(0, MAX_CELLS_TO_RENDER)
        .map((row) => row.slice(0, MAX_CELLS_TO_RENDER));

      const rows: CellData[][] = limitedData.map((row, rowIndex) =>
        row.map((cellValue: any) => ({
          value: sanitizeCellValue(cellValue),
          bold: rowIndex === 0,
        }))
      );

      worksheetsData.push({
        name: String(sheetName).substring(0, 100),
        rows,
      });
    });

    setWorksheets(worksheetsData);
    setActiveTab(0);
  }, [workbook]);

  if (error) {
    return <FilePreviewError onDownload={onDownload} />;
  }

  if (loading) {
    return (
      <Box sx={LOADING_SX}>
        <CircularProgress />
      </Box>
    );
  }

  const currentWorksheet = worksheets[activeTab];

  return (
    <Box sx={CONTAINER_SX}>
      {worksheets.length > 1 && (
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant='scrollable'
          scrollButtons='auto'
          sx={TABS_SX}
        >
          {worksheets.map((ws, index) => (
            <Tab key={index} label={ws.name} />
          ))}
        </Tabs>
      )}
      <Box sx={CONTENT_SX}>
        {currentWorksheet && (
          <Box sx={TABLE_WRAPPER_SX}>
            <table style={TABLE_STYLE}>
              <tbody>
                {currentWorksheet.rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex} style={getCellStyle(rowIndex, !!cell.bold)}>
                        {cell.value}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Box>
    </Box>
  );
};
